/**
 * Response actions — copy and insert AI response content.
 *
 * Provides handlers for:
 *   - Copy: Copy markdown response to clipboard with HTML formatting
 *   - Insert: Insert markdown response into Word at cursor position with formatting
 */

import { copyToClipboard, markdownToHtml, markdownToPlainText } from "./markdownToHtml";

/* global Word */

/**
 * Copy response content to clipboard with formatted HTML.
 * Converts markdown to both HTML and plain text for cross-application compatibility.
 * When pasted into Word, the HTML formatting is preserved.
 *
 * @param markdown - The markdown content to copy
 * @returns Promise that resolves when copy completes
 */
export async function copyResponseToClipboard(markdown: string): Promise<void> {
  try {
    const html = markdownToHtml(markdown);
    const plainText = markdownToPlainText(markdown);
    await copyToClipboard(plainText, html);
  } catch (err) {
    console.error("[responseActions] Failed to copy to clipboard:", err);
    throw new Error("Failed to copy response. Please try again.");
  }
}

/**
 * Insert response content into Word document at cursor position with formatting.
 * Converts markdown to HTML and inserts as formatted content at the active selection.
 *
 * @param markdown - The markdown content to insert
 * @returns Promise that resolves when insert completes
 */
export async function insertResponseIntoWord(markdown: string): Promise<void> {
  if (!isOfficeAvailable()) {
    throw new Error("Word add-in is not available in this context.");
  }

  const html = markdownToHtml(markdown);

  try {
    await Word.run(async (context) => {
      const selection = context.document.getSelection();

      // Capture the insertion range's own formatting BEFORE writing. insertHtml
      // stamps the HTML's baked-in styles (markdownToHtml hard-codes heading
      // sizes, colors and weights), so without this the inserted text looks
      // foreign to the paragraph it lands in. Read the parent paragraph's style
      // and the range's resolved base font, then re-apply them over the inserted
      // range so it matches "the text already there".
      const font = selection.font;
      font.load("name,size,color");
      const para = selection.paragraphs.getFirstOrNullObject();
      para.load("style,styleBuiltIn,isNullObject");
      await context.sync();

      const baseName = font.name;
      const baseSize = font.size;
      const baseColor = font.color;
      const baseStyle = para.isNullObject ? "" : para.style;
      const baseStyleBuiltIn = para.isNullObject ? "" : para.styleBuiltIn;

      const inserted = selection.insertHtml(html, "Replace");

      // Paragraph style first (clears the heading/list styling the HTML added),
      // then the base character font on top — so bold/italic runs from the
      // markdown survive while family/size/color track the surrounding text.
      if (baseStyle) {
        inserted.style = baseStyle;
      } else if (baseStyleBuiltIn) {
        inserted.styleBuiltIn = baseStyleBuiltIn;
      }
      if (baseName) inserted.font.name = baseName;
      if (typeof baseSize === "number" && baseSize > 0) inserted.font.size = baseSize;
      if (baseColor) inserted.font.color = baseColor;

      await context.sync();
    });
  } catch (err) {
    console.error("[responseActions] Failed to insert into Word:", err);
    // Fallback to plain text insertion if HTML insertion fails. Plain text
    // natively inherits the insertion point's formatting, so it already matches
    // the surrounding text.
    try {
      const plainText = markdownToPlainText(markdown);
      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.insertText(plainText, "Replace");
        await context.sync();
      });
    } catch (fallbackErr) {
      console.error("[responseActions] Fallback plain text insertion also failed:", fallbackErr);
      throw new Error("Failed to insert response into Word. Please try again.");
    }
  }
}

/**
 * Check if Office JS is available.
 */
function isOfficeAvailable(): boolean {
  try {
    const WordGlobal = (globalThis as Record<string, unknown>).Word;
    return typeof WordGlobal !== "undefined" && typeof (WordGlobal as { run: unknown }).run === "function";
  } catch {
    return false;
  }
}

/**
 * Generic response action handler with error handling.
 * Routes copy/insert actions to appropriate handlers.
 *
 * @param action - The action to perform ('copy' or 'insert')
 * @param content - The markdown content to process
 * @param onSuccess - Optional callback on success
 * @param onError - Optional callback on error (if provided, prevents throw)
 */
export async function handleResponseAction(
  action: "copy" | "insert",
  content: string,
  onSuccess?: () => void,
  onError?: (error: Error) => void,
): Promise<void> {
  try {
    if (action === "copy") {
      await copyResponseToClipboard(content);
      onSuccess?.();
    } else if (action === "insert") {
      await insertResponseIntoWord(content);
      onSuccess?.();
    } else {
      throw new Error(`Unknown action: ${action}`);
    }
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}
